import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountBalance,
  ArrowBackIosNew,
  Badge as BadgeIcon,
  Cancel,
  CheckCircle,
  CreditCard,
  Email,
  LocationOn,
  Person,
  Phone,
  Verified,
  Visibility,
  Warning,
  Work,
} from "@mui/icons-material";
import ProviderDocumentReviewList from "../documentUploads/ProviderDocumentReviewList";
import {
  approveIndiaProvider,
  getIndiaProvider,
  getIndiaProviderDocuments,
  rejectIndiaProvider,
  revealIndiaKyc,
  reviewIndiaProviderDocument,
} from "../../utils/indiaAdminService";

// India document slots (client decision 2026-10-09: Aadhaar + PAN required).
// Keys follow the shared document-type normalisation (resume_cv -> resume, etc).
export const INDIA_DOCUMENT_GROUPS = [
  { key: "aadhaar", label: "Aadhaar Card", aliases: ["aadhaar"], legacyKeys: [] },
  { key: "pan", label: "PAN Card", aliases: ["pan"], legacyKeys: [] },
  { key: "certificates", label: "Certificates", aliases: ["certificates"], legacyKeys: [] },
  { key: "resume", label: "Resume/CV", aliases: ["resume", "resume_cv"], legacyKeys: [] },
  { key: "selfie_photo", label: "Selfie Photo", aliases: ["selfie_photo"], legacyKeys: [] },
  {
    key: "verificationVideo",
    label: "Selfie Video",
    aliases: ["verification_video"],
    legacyKeys: [],
  },
];

const REJECTION_REASON_OPTIONS = [
  { value: "aadhaar_issue", label: "Aadhaar number or card is missing, unclear, or invalid" },
  { value: "pan_issue", label: "PAN or PAN card is missing, unclear, or invalid" },
  { value: "banking_details_issue", label: "Banking details are incomplete or incorrect" },
  { value: "resume_missing", label: "Resume/CV is missing or not acceptable" },
  { value: "certificate_missing", label: "Certificates are missing, expired, or unclear" },
  { value: "selfie_issue", label: "Selfie photo or video is missing or not clear" },
  { value: "profile_info_incomplete", label: "Profile information is incomplete" },
];

const REUPLOAD_OPTIONS = [
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "pan", label: "PAN Card" },
  { value: "resume", label: "Resume/CV" },
  { value: "certificates", label: "Certificates" },
  { value: "selfiePhoto", label: "Selfie Photo" },
  { value: "verificationVideo", label: "Selfie Video" },
  { value: "kycDetails", label: "Aadhaar/PAN Numbers" },
  { value: "bankingDetails", label: "Banking Details" },
];

const KYC_FIELD_LABELS = {
  aadhaar: "Aadhaar Number",
  pan: "PAN",
  bankAccountNumber: "Account Number",
};

const createEmptyRejectionForm = () => ({
  reasons: [],
  customReason: "",
  requestReupload: false,
  reuploadItems: [],
  missingDetails: "",
});

const NotAdded = () => (
  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
    Not added yet
  </Typography>
);

const InfoRow = ({ icon: Icon, label, value, action }) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
    <Icon sx={{ color: "text.secondary" }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2">{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography component="div" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {value || <NotAdded />}
        </Typography>
        {action}
      </Box>
    </Box>
  </Box>
);

const SectionCard = ({ icon: Icon, title, chip, children }) => (
  <Grid item size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex", minWidth: 0 }}>
    <Paper
      variant="outlined"
      sx={{ p: 2, width: "100%", height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Icon sx={{ fontSize: 20 }} />
          <Typography variant="h6">{title}</Typography>
        </Box>
        {chip}
      </Box>
      <Box sx={{ mt: 2, display: "grid", gap: 1.5, flex: 1 }}>{children}</Box>
    </Paper>
  </Grid>
);

const formatAddress = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address;
  return [address.line1, address.line2, address.city, address.state, address.pincode]
    .filter(Boolean)
    .join(", ");
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const capitalize = (value) =>
  typeof value === "string" && value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const statusColor = (status) =>
  status === "approved" ? "success" : status === "pending" ? "warning" : status === "rejected" ? "error" : "default";

const serviceLabel = (service) =>
  typeof service === "string"
    ? service
    : service?.name || service?.title || service?.serviceName || service?.id || "";

/**
 * India version of the provider "View" dialog on the Request / Provider pages.
 * Same layout as Australia; data comes from the India admin callables.
 * `mode` "approve" / "reject" opens only that confirmation (card buttons).
 */
const IndiaProviderProfileDialog = ({
  open,
  providerId,
  providerName = "",
  mode = "view",
  onClose,
  onStatusChanged,
  notify,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [revealed, setRevealed] = useState({});
  const [revealDialog, setRevealDialog] = useState({ open: false, field: "", reason: "" });
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState("");

  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [rejectionForm, setRejectionForm] = useState(createEmptyRejectionForm());
  const [submitting, setSubmitting] = useState(false);

  // `silent` refreshes after a document review without hiding the dialog content.
  const load = async (silent = false) => {
    if (!providerId) return;
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      setData(await getIndiaProvider(providerId));
    } catch (err) {
      if (!silent) {
        setError(err.message || "Could not load this provider.");
        setData(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !providerId) return;
    setRevealed({});
    setConfirmApproveOpen(mode === "approve");
    setRejectionForm(createEmptyRejectionForm());
    setRejectionOpen(mode === "reject");
    if (mode === "view") {
      load();
    } else {
      setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, providerId, mode]);

  // In approve/reject mode the confirmation is the whole dialog.
  const closeConfirmation = (setter) => {
    setter(false);
    if (mode !== "view") onClose?.();
  };

  const submitReveal = async () => {
    setRevealing(true);
    setRevealError("");
    try {
      const result = await revealIndiaKyc(providerId, revealDialog.field, revealDialog.reason);
      setRevealed((prev) => ({ ...prev, [revealDialog.field]: result.value }));
      setRevealDialog({ open: false, field: "", reason: "" });
    } catch (err) {
      setRevealError(err.message || "Could not reveal this value.");
    } finally {
      setRevealing(false);
    }
  };

  const revealButton = (field, hasValue) =>
    hasValue && !revealed[field] ? (
      <Button
        size="small"
        startIcon={<Visibility fontSize="small" />}
        onClick={() => {
          setRevealError("");
          setRevealDialog({ open: true, field, reason: "" });
        }}
      >
        Reveal
      </Button>
    ) : null;

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await approveIndiaProvider(providerId);
      notify?.(`Client ${displayName || "Provider"} has been approved successfully`, "success");
      setConfirmApproveOpen(false);
      onStatusChanged?.(providerId, "approved");
      onClose?.();
    } catch (err) {
      notify?.(err.message || "Could not approve this provider.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleIn = (key, value) =>
    setRejectionForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((item) => item !== value)
        : [...prev[key], value],
    }));

  const handleReject = async () => {
    const labelOf = (options, value) => options.find((o) => o.value === value)?.label || value;
    const customReason = rejectionForm.customReason.trim();
    const reasons = [
      ...rejectionForm.reasons.map((value) => labelOf(REJECTION_REASON_OPTIONS, value)),
      ...(customReason ? [customReason] : []),
    ];
    const reuploadItems = rejectionForm.requestReupload
      ? rejectionForm.reuploadItems.map((value) => labelOf(REUPLOAD_OPTIONS, value))
      : [];
    const missingDetails = rejectionForm.missingDetails
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (reasons.length === 0) {
      notify?.("Please select at least one rejection reason", "warning");
      return;
    }

    setSubmitting(true);
    try {
      await rejectIndiaProvider(providerId, { reasons, reuploadItems, missingDetails });
      notify?.(`Client ${displayName || "Provider"} has been rejected`, "success");
      setRejectionOpen(false);
      setRejectionForm(createEmptyRejectionForm());
      onStatusChanged?.(providerId, "rejected");
      onClose?.();
    } catch (err) {
      notify?.(err.message || "Could not reject this provider.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const top = data?.provider || {};
  const displayName = top.name || providerName;
  const onboarding = data?.onboarding || {};
  const kyc = onboarding.kyc || {};
  const bank = onboarding.bankingDetails || {};
  const profile = onboarding.profile || {};
  const services = (profile.servicesOffered || []).map(serviceLabel).filter(Boolean);
  const missingApprovals = data?.missingApprovals || [];
  const approvalStatus = top.approvalStatus;
  const bankVerified = bank.verificationStatus === "verified";

  return (
    <>
      <Dialog
        open={open && mode === "view"}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { maxWidth: 1500, width: "calc(100% - 32px)", overflowX: "hidden" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button startIcon={<ArrowBackIosNew />} onClick={onClose}>
              Back to Dashboard
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: "hidden" }}>
          {loading ? (
            <div>Loading...</div>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : data ? (
            <Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
                <Avatar sx={{ width: 64, height: 64 }} src={top.photoURL || top.profileImage}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6">{top.name || "Unknown"}</Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                    <Chip label="India" size="small" variant="outlined" />
                    {top.inactive ? (
                      <Chip label="Inactive" size="small" color="error" />
                    ) : (
                      <Chip label="Active" size="small" color="primary" />
                    )}
                    {approvalStatus && (
                      <Chip
                        label={capitalize(approvalStatus)}
                        size="small"
                        color={statusColor(approvalStatus)}
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              {approvalStatus !== "approved" && missingApprovals.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Approve these documents before approving the application:{" "}
                  {missingApprovals.join(", ")}.
                </Alert>
              )}
              {approvalStatus === "rejected" && top.rejectionReason && (
                <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                  {top.rejectionReason}
                </Alert>
              )}

              <ProviderDocumentReviewList
                providerId={providerId}
                documentGroups={INDIA_DOCUMENT_GROUPS}
                fetchDocuments={({ providerId: id }) => getIndiaProviderDocuments(id)}
                reviewDocument={async (payload) => {
                  const result = await reviewIndiaProviderDocument(payload);
                  load(true);
                  return result;
                }}
              />

              <Grid container spacing={2} alignItems="stretch" sx={{ mt: 0 }}>
                <SectionCard icon={Person} title="Personal Information">
                  <InfoRow icon={Email} label="Email" value={top.email} />
                  <InfoRow icon={Phone} label="Phone" value={top.phoneNumber || top.phone} />
                  <InfoRow icon={LocationOn} label="Address" value={formatAddress(top.address)} />
                </SectionCard>

                <SectionCard
                  icon={BadgeIcon}
                  title="Identity (KYC)"
                  chip={
                    kyc.aadhaarMasked && kyc.panMasked ? (
                      <Chip icon={<Verified />} label="Submitted" size="small" color="success" />
                    ) : (
                      <Chip icon={<Warning />} label="Incomplete" size="small" color="warning" />
                    )
                  }
                >
                  <InfoRow
                    icon={BadgeIcon}
                    label="Aadhaar Number"
                    value={revealed.aadhaar || kyc.aadhaarMasked}
                    action={revealButton("aadhaar", kyc.aadhaarMasked)}
                  />
                  <InfoRow
                    icon={CreditCard}
                    label="PAN"
                    value={revealed.pan || kyc.panMasked}
                    action={revealButton("pan", kyc.panMasked)}
                  />
                  <InfoRow icon={BadgeIcon} label="Submitted On" value={formatDate(kyc.submittedAt)} />
                </SectionCard>

                <SectionCard
                  icon={AccountBalance}
                  title="Banking Details"
                  chip={
                    bankVerified ? (
                      <Chip icon={<Verified />} label="Verified" size="small" color="success" />
                    ) : (
                      <Chip icon={<Warning />} label="Unverified" size="small" color="warning" />
                    )
                  }
                >
                  <InfoRow icon={AccountBalance} label="Bank Name" value={bank.bankName} />
                  <InfoRow icon={AccountBalance} label="Account Name" value={bank.accountHolderName} />
                  <InfoRow icon={CreditCard} label="IFSC" value={bank.ifsc} />
                  <InfoRow
                    icon={CreditCard}
                    label="Account Number"
                    value={revealed.bankAccountNumber || bank.accountNumberMasked}
                    action={revealButton("bankAccountNumber", bank.accountNumberMasked)}
                  />
                  <InfoRow icon={AccountBalance} label="Branch" value={bank.branch} />
                  <InfoRow icon={CreditCard} label="Account Type" value={capitalize(bank.accountType)} />
                </SectionCard>

                <SectionCard icon={Work} title="Services & Experience">
                  <InfoRow icon={Work} label="Experience" value={onboarding.experience || profile.experience} />
                  <InfoRow icon={Person} label="Preferred Customer Gender" value={capitalize(profile.preferredGender)} />
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Services Offered
                    </Typography>
                    {services.length ? (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {services.map((name) => (
                          <Chip key={name} label={name} size="small" />
                        ))}
                      </Box>
                    ) : (
                      <NotAdded />
                    )}
                  </Box>
                </SectionCard>
              </Grid>
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              {approvalStatus !== "approved" && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  disabled={!data || missingApprovals.length > 0}
                  onClick={() => setConfirmApproveOpen(true)}
                >
                  Approve
                </Button>
              )}
              {approvalStatus !== "rejected" && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  disabled={!data}
                  onClick={() => {
                    setRejectionForm(createEmptyRejectionForm());
                    setRejectionOpen(true);
                  }}
                >
                  Reject
                </Button>
              )}
            </Box>
            <Button onClick={onClose}>Close</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Approve confirmation (same wording as Australia) */}
      <Dialog
        open={open && confirmApproveOpen}
        onClose={() => !submitting && closeConfirmation(setConfirmApproveOpen)}
      >
        <DialogTitle>Approve Client Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to <strong>approve</strong> {displayName}'s application?
            <br />
            <br />
            Once approved, they will be able to login and access all client features immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeConfirmation(setConfirmApproveOpen)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            autoFocus
            color="success"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection dialog (same layout as Australia) */}
      <Dialog
        open={open && rejectionOpen}
        onClose={() => !submitting && closeConfirmation(setRejectionOpen)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Provider Application</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select one or more rejection reasons. You can also request re-upload of documents
            or ask for missing details. The provider will see this in their application.
          </DialogContentText>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Rejection reasons (multiple allowed)
          </Typography>
          <FormGroup sx={{ mb: 2 }}>
            {REJECTION_REASON_OPTIONS.map((reason) => (
              <FormControlLabel
                key={reason.value}
                control={
                  <Checkbox
                    checked={rejectionForm.reasons.includes(reason.value)}
                    onChange={() => toggleIn("reasons", reason.value)}
                  />
                }
                label={reason.label}
              />
            ))}
          </FormGroup>

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Additional rejection reason (optional)"
            placeholder="Add any additional reason not listed above"
            value={rejectionForm.customReason}
            onChange={(e) => setRejectionForm((prev) => ({ ...prev, customReason: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <Divider sx={{ mb: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={rejectionForm.requestReupload}
                onChange={(e) =>
                  setRejectionForm((prev) => ({
                    ...prev,
                    requestReupload: e.target.checked,
                    reuploadItems: e.target.checked ? prev.reuploadItems : [],
                  }))
                }
              />
            }
            label="Request re-upload of documents"
            sx={{ mb: 1 }}
          />

          {rejectionForm.requestReupload && (
            <FormGroup sx={{ mb: 2, pl: 1 }}>
              {REUPLOAD_OPTIONS.map((item) => (
                <FormControlLabel
                  key={item.value}
                  control={
                    <Checkbox
                      checked={rejectionForm.reuploadItems.includes(item.value)}
                      onChange={() => toggleIn("reuploadItems", item.value)}
                    />
                  }
                  label={item.label}
                />
              ))}
            </FormGroup>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Missing details to update (optional)"
            placeholder={"One detail per line. Example:\nUpload a clearer Aadhaar back side\nUpdate account holder name"}
            value={rejectionForm.missingDetails}
            onChange={(e) => setRejectionForm((prev) => ({ ...prev, missingDetails: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeConfirmation(setRejectionOpen)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={
              submitting ||
              (rejectionForm.reasons.length === 0 && !rejectionForm.customReason.trim())
            }
            startIcon={submitting ? <CircularProgress size={16} /> : <Cancel />}
          >
            {submitting ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* KYC reveal (India only: numbers are encrypted and every view is logged) */}
      <Dialog
        open={revealDialog.open}
        onClose={() => !revealing && setRevealDialog({ open: false, field: "", reason: "" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Reveal {KYC_FIELD_LABELS[revealDialog.field]}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This number is sensitive. Your access and reason are recorded in the audit log.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Reason"
            placeholder="e.g. Verifying against the uploaded Aadhaar card"
            value={revealDialog.reason}
            onChange={(e) => setRevealDialog((prev) => ({ ...prev, reason: e.target.value }))}
          />
          {revealError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revealError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRevealDialog({ open: false, field: "", reason: "" })}
            disabled={revealing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitReveal}
            disabled={revealing || revealDialog.reason.trim().length < 5}
          >
            {revealing ? <CircularProgress size={18} color="inherit" /> : "Reveal"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IndiaProviderProfileDialog;
